/**
 * Helpers for translating selected rightsholder / placement ids between the
 * two attribution methodologies ("team" and "league").
 *
 * Each methodology returns its own ids from `/rightsholders?...` (with or
 * without `useRightsholderAttribution=true`). A placement called "Courtside
 * Signs" might be `26-42` under team mode and `26-6121` under league mode.
 *
 * These helpers translate by **name** (not id), using the rightsholder name
 * + placement name as the matching key. They were originally inlined in
 * `actions/filters/getFilters.js`; extracted here so other call sites can
 * reuse them when comparing or transforming selections across lists.
 */

export const resolveLeagueRightsholderId = (
    leagueId,
    leagueModeList = {},
    teamModeList = null,
) => {
    const leagueIdStr = String(leagueId);
    const rhsInLeague = Object.entries(leagueModeList).filter(
        ([, rh]) => String(rh?.leagueId) === leagueIdStr,
    );

    const byId = rhsInLeague.find(([, rh]) => String(rh.id) === leagueIdStr);
    if (byId) return byId[0];

    const byLeagueName = rhsInLeague.find(
        ([, rh]) => rh.leagueName && rh.name === rh.leagueName,
    );
    if (byLeagueName) return byLeagueName[0];

    if (teamModeList) {
        const teamNames = new Set(
            Object.values(teamModeList)
                .filter(rh => String(rh?.leagueId) === leagueIdStr)
                .map(rh => rh.name),
        );
        const leagueOnlyCandidates = rhsInLeague.filter(
            ([, rh]) => !teamNames.has(rh.name),
        );

        if (leagueOnlyCandidates.length === 1) {
            return leagueOnlyCandidates[0][0];
        }
    }

    return null;
};

export const isLeagueRightsholder = (
    rh = {},
    { leagueModeList = null, teamModeList = null } = {},
) => {
    if (rh?.leagueId == null || rh.leagueId === '') return false;

    const id = String(rh.id);
    const leagueId = String(rh.leagueId);

    if (id === leagueId) return true;
    if (rh.leagueName && rh.name === rh.leagueName) return true;

    if (leagueModeList) {
        const resolvedId = resolveLeagueRightsholderId(
            rh.leagueId,
            leagueModeList,
            teamModeList,
        );

        if (resolvedId && String(resolvedId) === id) return true;
    }

    return false;
};

const groupTeamRightsholdersByLeague = (
    rightsholdersList = {},
    { leagueModeList = null, teamModeList = null } = {},
) =>
    Object.entries(rightsholdersList).reduce((acc, [id, rh]) => {
        if (
            isLeagueRightsholder(rh, {
                leagueModeList: leagueModeList || rightsholdersList,
                teamModeList,
            })
        ) {
            return acc;
        }

        const leagueKey = rh?.leagueId != null ? String(rh.leagueId) : 'other';
        if (!acc[leagueKey]) acc[leagueKey] = [];
        acc[leagueKey].push(String(id));
        return acc;
    }, {});

const getLeagueRightsholderIdsByLeague = (
    leagueModeList = {},
    teamModeList = null,
) => {
    const leagueIds = [
        ...new Set(
            Object.values(leagueModeList)
                .map(rh => rh?.leagueId)
                .filter(leagueId => leagueId != null && leagueId !== '')
                .map(String),
        ),
    ];

    return leagueIds.reduce((acc, leagueId) => {
        const resolvedId = resolveLeagueRightsholderId(
            leagueId,
            leagueModeList,
            teamModeList,
        );

        if (resolvedId) acc[leagueId] = resolvedId;

        return acc;
    }, {});
};

const hasFullLeagueTeamSelection = (selectedIds = [], teamIds = []) => {
    if (!teamIds.length) return false;

    const selectedSet = new Set(selectedIds.map(String));
    return teamIds.every(teamId => selectedSet.has(String(teamId)));
};

/**
 * When switching attribution modes, preserve "full league" selections:
 * - team -> league: if every team RH in a league was selected, also select the league RH
 * - league -> team: drop league RHs while keeping all team RHs selected
 */
export const applyFullLeagueSelectionRules = ({
    selectedRightsholderIds = [],
    preTranslationSelectedIds = [],
    sourceRightsholdersList = {},
    targetRightsholdersList = {},
    sourceAttributionMode,
    targetAttributionMode,
}) => {
    if (sourceAttributionMode === targetAttributionMode) {
        return [...new Set(selectedRightsholderIds.map(String))];
    }

    const selectedSet = new Set(selectedRightsholderIds.map(String));
    const preTranslationSet = new Set(preTranslationSelectedIds.map(String));
    const teamModeList =
        sourceAttributionMode === 'team'
            ? sourceRightsholdersList
            : targetAttributionMode === 'team'
              ? targetRightsholdersList
              : null;
    const leagueModeListForTarget =
        targetAttributionMode === 'league'
            ? targetRightsholdersList
            : sourceRightsholdersList;
    const leagueModeListForSource =
        sourceAttributionMode === 'league'
            ? sourceRightsholdersList
            : targetRightsholdersList;
    const sourceTeamsByLeague = groupTeamRightsholdersByLeague(
        sourceRightsholdersList,
        {
            leagueModeList: leagueModeListForSource,
            teamModeList,
        },
    );
    const targetTeamsByLeague = groupTeamRightsholdersByLeague(
        targetRightsholdersList,
        {
            leagueModeList: leagueModeListForTarget,
            teamModeList,
        },
    );
    const sourceLeagueRhByLeague = getLeagueRightsholderIdsByLeague(
        leagueModeListForSource,
        teamModeList,
    );
    const targetLeagueRhByLeague = getLeagueRightsholderIdsByLeague(
        leagueModeListForTarget,
        teamModeList,
    );

    if (targetAttributionMode === 'league') {
        Object.entries(sourceTeamsByLeague).forEach(
            ([leagueId, sourceTeamIds]) => {
                if (
                    !hasFullLeagueTeamSelection(
                        preTranslationSelectedIds,
                        sourceTeamIds,
                    )
                ) {
                    return;
                }

                const leagueRhId = targetLeagueRhByLeague[leagueId];
                if (leagueRhId) {
                    selectedSet.add(leagueRhId);
                }

                (targetTeamsByLeague[leagueId] || []).forEach(teamId =>
                    selectedSet.add(teamId),
                );
            },
        );
    } else {
        Object.entries(sourceLeagueRhByLeague).forEach(
            ([leagueId, leagueRhId]) => {
                const sourceTeamIds = sourceTeamsByLeague[leagueId] || [];
                const targetTeamIds = targetTeamsByLeague[leagueId] || [];
                const hadLeagueRh = preTranslationSet.has(leagueRhId);
                const hadAllSourceTeams = hasFullLeagueTeamSelection(
                    preTranslationSelectedIds,
                    sourceTeamIds,
                );

                selectedSet.delete(leagueRhId);

                if (hadLeagueRh || hadAllSourceTeams) {
                    targetTeamIds.forEach(teamId => selectedSet.add(teamId));
                }
            },
        );

        Object.values(targetLeagueRhByLeague).forEach(leagueRhId => {
            selectedSet.delete(leagueRhId);
        });
    }

    return [...selectedSet].filter(id =>
        Object.prototype.hasOwnProperty.call(targetRightsholdersList, id),
    );
};

export const translateRightsholderIdsByName = (
    selectedRightsholderIds = [],
    oldRightsholdersList = {},
    newRightsholdersList = {},
) => {
    const newRhIdByName = Object.values(newRightsholdersList).reduce(
        (acc, rh) => {
            if (rh?.name && rh?.id !== undefined && rh?.id !== null) {
                acc[rh.name] = String(rh.id);
            }
            return acc;
        },
        {},
    );

    const translated = selectedRightsholderIds.reduce((acc, oldRhId) => {
        const oldRh = oldRightsholdersList[oldRhId];
        if (oldRh?.name && newRhIdByName[oldRh.name]) {
            acc.push(newRhIdByName[oldRh.name]);
            return acc;
        }

        if (
            Object.prototype.hasOwnProperty.call(newRightsholdersList, oldRhId)
        ) {
            acc.push(String(oldRhId));
        }

        return acc;
    }, []);

    return [...new Set(translated)];
};

export const buildPlacementIdByRhAndName = (placementsList = {}) =>
    Object.values(placementsList).reduce((acc, placement) => {
        if (!placement?.id || !placement?.name) return acc;
        const [rhId] = String(placement.id).split('-');
        if (!rhId) return acc;
        acc[`${rhId}::${placement.name}`] = String(placement.id);
        return acc;
    }, {});

export const translatePlacementIdsByName = ({
    selectedPlacementIds = [],
    oldPlacementsList = {},
    newPlacementsList = {},
    rightsholderIdMap = {},
    translatedRightsholderIds = [],
    enforceMatchedRightsholders = true,
}) => {
    const translatedRhSet = new Set(translatedRightsholderIds.map(String));
    const newPlacementIdByRhAndName =
        buildPlacementIdByRhAndName(newPlacementsList);

    const translated = selectedPlacementIds.reduce((acc, oldPlacementId) => {
        const [oldRhId] = String(oldPlacementId).split('-');
        if (!oldRhId) return acc;

        const mappedRhId =
            rightsholderIdMap[String(oldRhId)] ?? String(oldRhId);
        if (
            enforceMatchedRightsholders &&
            translatedRhSet.size > 0 &&
            !translatedRhSet.has(mappedRhId)
        ) {
            return acc;
        }

        const oldPlacement = oldPlacementsList[oldPlacementId];
        const placementName = oldPlacement?.name;

        if (placementName) {
            const newPlacementId =
                newPlacementIdByRhAndName[`${mappedRhId}::${placementName}`];
            if (newPlacementId) {
                acc.push(newPlacementId);
            }
            return acc;
        }

        const directPlacement = newPlacementsList[oldPlacementId];
        if (directPlacement) {
            acc.push(String(oldPlacementId));
        }

        return acc;
    }, []);

    return [...new Set(translated)];
};

/**
 * Builds a `{ [oldRhId]: newRhId }` map for every rightsholder in the source
 * list that has a same-named entry in the target list.
 */
export const buildRightsholderIdMap = (
    sourceRightsholdersList = {},
    targetRightsholdersList = {},
) => {
    const newRhIdByName = Object.values(targetRightsholdersList).reduce(
        (acc, rh) => {
            if (rh?.name && rh?.id !== undefined && rh?.id !== null) {
                acc[rh.name] = String(rh.id);
            }
            return acc;
        },
        {},
    );

    return Object.keys(sourceRightsholdersList).reduce((acc, oldRhId) => {
        const oldRhName = sourceRightsholdersList[oldRhId]?.name;
        if (!oldRhName) return acc;
        const newRhId = newRhIdByName[oldRhName];
        if (newRhId) acc[String(oldRhId)] = newRhId;
        return acc;
    }, {});
};

/**
 * High-level helper: given the user's currently selected rightsholder ids
 * and rightsholderPlacement ids (as expected by API payloads), translate
 * them across attribution modes by matching on rightsholder/placement
 * names.
 *
 * Returns `{ rightsholderIds, rightsholderPlacementIds }` containing only
 * ids that exist in the target lists. If a selection has no equivalent in
 * the target mode it is dropped (rather than leaving an invalid id in the
 * payload).
 */
export const translateSelectedFilterIds = ({
    rightsholderIds = [],
    rightsholderPlacementIds = [],
    sourceRightsholdersList = {},
    sourcePlacementsList = {},
    targetRightsholdersList = {},
    targetPlacementsList = {},
    sourceAttributionMode = 'team',
    targetAttributionMode = 'team',
}) => {
    const translatedRightsholderIds = translateRightsholderIdsByName(
        rightsholderIds,
        sourceRightsholdersList,
        targetRightsholdersList,
    );

    const normalizedRightsholderIds = applyFullLeagueSelectionRules({
        selectedRightsholderIds: translatedRightsholderIds,
        preTranslationSelectedIds: rightsholderIds,
        sourceRightsholdersList,
        targetRightsholdersList,
        sourceAttributionMode,
        targetAttributionMode,
    });

    const rightsholderIdMap = buildRightsholderIdMap(
        sourceRightsholdersList,
        targetRightsholdersList,
    );

    const translatedRightsholderPlacementIds = translatePlacementIdsByName({
        selectedPlacementIds: rightsholderPlacementIds,
        oldPlacementsList: sourcePlacementsList,
        newPlacementsList: targetPlacementsList,
        rightsholderIdMap,
        translatedRightsholderIds: normalizedRightsholderIds,
        enforceMatchedRightsholders: rightsholderIds.length > 0,
    });

    return {
        rightsholderIds: normalizedRightsholderIds,
        rightsholderPlacementIds: translatedRightsholderPlacementIds,
    };
};
